import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IUsageType } from "../interface/IUsageType";
import { getUsageDetailsRoute, getUsageSummaryRoute, getUsageTypeListRoute } from "@api-routes/cdrUsageConsumption";
import { IUsageSummary } from "../interface/IUsageSummary";
import { format, subMonths } from "date-fns";
import { RECORDS_PER_PAGE } from "@constants";
import { IUsageDetail } from "../interface/IUsageDetail";
import { getFormattedQueryFromState } from "../usageUtils";
import { IFromToDate } from "../interface/IFromToDate";
import { getResponseToState as getUsageTypeResponseToState, IUsageTypeResponse } from "../mappers/getUsageTypeMapper";
import {
    getResponseToState as getUsageSummaryListResponseToState,
    IUsageSummaryListResponse,
} from "../mappers/getUsageSummaryMapper";
import {
    getResponseToState as getUsageDetailListResponseToState,
    IUsageDetailListResponse,
} from "../mappers/getUsageDetailMapper";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { useFetch } from "@common/hook/useFetch";

export const useUsage = () => {
    const { t } = useTranslation("usage");

    const { selectedService } = useContext(SelectedServiceContext);

    const date = new Date();
    const timeFilterOptions = [
        { label: t("lastMonth"), value: format(date, "yyyyMM") },
        { label: format(subMonths(date, 1), "MMMM"), value: format(subMonths(date, 1), "yyyyMM") },
        { label: format(subMonths(date, 2), "MMMM"), value: format(subMonths(date, 2), "yyyyMM") },
    ];

    const [selectedTimePeriod, setSelectedTimePeriod] = useState(timeFilterOptions[0].value);
    const [selectedType, setSelectedType] = useState(t("all"));
    const [selectedPageSize, setSelectedPageSize] = useState(RECORDS_PER_PAGE[0]);
    const [fromToDate, setFromToDate] = useState<IFromToDate>({ startDate: "", endDate: "" });
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [usageTypeList, setUsageTypeList] = useState<IUsageType[]>([]);
    const [usageSummaryList, setUsageSummaryList] = useState<IUsageSummary[]>([]);
    const [usageDetailList, setUsageDetailList] = useState<IUsageDetail[]>([]);

    const [getUsageTypeListState, { get }] = useFetch();
    const [getUsageSummaryState, { get: getUsageSummary }] = useFetch();
    const [getUsageDetailListState, { get: getUsageDetailList }] = useFetch();

    const getUsageTypeList = useCallback(() => {
        get<IUsageTypeResponse>(getUsageTypeListRoute()).then((usageTypeListResponse) => {
            if (usageTypeListResponse) {
                setUsageTypeList(getUsageTypeResponseToState(usageTypeListResponse));
            }
        });
    }, [get]);

    const refreshUsageDetailList = useCallback(() => {
        if (selectedService?.id) {
            getUsageDetailList<IUsageDetailListResponse>(
                getUsageDetailsRoute(
                    selectedTimePeriod,
                    selectedService.id,
                    getFormattedQueryFromState(currentPage, selectedType, selectedPageSize)
                )
            ).then((usageDetailResponse) => {
                if (usageDetailResponse) {
                    const usageDetailListMapped = getUsageDetailListResponseToState(usageDetailResponse);

                    setUsageDetailList(usageDetailListMapped.content);
                    setTotalPages(usageDetailListMapped.totalPages);
                }
            });
        }
    }, [selectedPageSize, selectedType, selectedTimePeriod, selectedService, currentPage, getUsageDetailList]);

    const refreshUsageSummary = useCallback(() => {
        if (selectedService?.id) {
            getUsageSummary<IUsageSummaryListResponse>(
                getUsageSummaryRoute(selectedService.id, selectedTimePeriod)
            ).then((usageSummaryListResponse) => {
                if (usageSummaryListResponse) {
                    const { billPeriodStartDate, billPeriodEndDate, usageSummary } = getUsageSummaryListResponseToState(
                        usageSummaryListResponse
                    );

                    setFromToDate({
                        startDate: billPeriodStartDate,
                        endDate: billPeriodEndDate,
                    });
                    setUsageSummaryList(usageSummary);
                }
            });
        }
    }, [selectedTimePeriod, selectedService, getUsageSummary]);

    const onTimePeriodChange = (timePeriod: string) => {
        setCurrentPage(0);
        setSelectedTimePeriod(timePeriod);
    };

    const onPageSizeChange = (pageSize: number) => {
        setCurrentPage(0);
        setSelectedPageSize(pageSize);
    };

    const onSelectedTypeChange = (type: string) => {
        setCurrentPage(0);
        setSelectedType(type);
    };

    useEffect(() => {
        getUsageTypeList();
    }, [getUsageTypeList]);

    useEffect(() => {
        refreshUsageDetailList();
    }, [refreshUsageDetailList]);

    useEffect(() => {
        refreshUsageSummary();
    }, [refreshUsageSummary]);

    return {
        fromToDate,
        selectedType,
        onSelectedTypeChange,
        selectedPageSize,
        getUsageTypeListState,
        getUsageTypeList,
        usageTypeList,
        selectedTimePeriod,
        onTimePeriodChange,
        onPageSizeChange,
        usageSummaryList,
        getUsageSummaryState,
        timeFilterOptions,
        usageDetailList,
        getUsageDetailListState,
        totalPages,
        setTotalPages,
        currentPage,
        setCurrentPage,
        refreshUsageDetailList,
        refreshUsageSummary,
    };
};
